var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import React, { forwardRef, useCallback, useState, useRef } from "react";
import TextareaAutosize from "react-textarea-autosize";
import { classnames, generateTestId } from "../../../utils";
import { InputContainer, IconButton, ErrorMessage } from "./styled";
var TextInput = forwardRef(function (_a, ref) {
    var _b = _a.isReadMode, isReadMode = _b === void 0 ? false : _b, _c = _a.isMultiLine, isMultiLine = _c === void 0 ? false : _c, onChange = _a.onChange, onClick = _a.onClick, _d = _a.hasError, hasError = _d === void 0 ? false : _d, errorMessage = _a.errorMessage, value = _a.value, onBlur = _a.onBlur, onFocus = _a.onFocus, _e = _a.placeholder, placeholder = _e === void 0 ? "" : _e, inputIcon = _a.inputIcon, onIconClick = _a.onIconClick, inlinePrefixElement = _a.inlinePrefixElement, iconButtonAttributes = _a.iconButtonAttributes, errorMessageAttributes = _a.errorMessageAttributes, minMultiLineRows = _a.minMultiLineRows, maxMultiLineRows = _a.maxMultiLineRows, hasReadModeStyles = _a.hasReadModeStyles, rest = __rest(_a, ["isReadMode", "isMultiLine", "onChange", "onClick", "hasError", "errorMessage", "value", "onBlur", "onFocus", "placeholder", "inputIcon", "onIconClick", "inlinePrefixElement", "iconButtonAttributes", "errorMessageAttributes", "minMultiLineRows", "maxMultiLineRows", "hasReadModeStyles"]);
    var defaultValue = rest.defaultValue, disabled = rest.disabled;
    var defaultInputRef = useRef(null);
    var inputIconButtonRef = useRef(null);
    var _ref = (ref ? ref : defaultInputRef);
    var _f = __read(useState(false), 2), isFocused = _f[0], setFocused = _f[1];
    var isUncontrolled = value === undefined;
    var _g = __read(useState(defaultValue || ""), 2), uncontrolledInputValue = _g[0], setUncontrolledInputValue = _g[1];
    var previousValue = React.useRef(value);
    React.useEffect(function warnIfUncontrolledToControlled() {
        if (typeof previousValue.current !== "undefined" &&
            typeof value === "undefined") {
            console.warn(new Error("Controlled input possibly becoming uncontrolled. This may be"));
        }
        else if (typeof previousValue.current === "undefined" &&
            typeof value !== "undefined") {
            console.warn(new Error("Uncontrolled input possibly becoming controlled. Do not set value."));
        }
        previousValue.current = value;
    }, [value]);
    var _onChange = useCallback(function (e) {
        var value = e.target.value;
        setUncontrolledInputValue(value);
        if (onChange)
            onChange(e);
    }, [onChange]);
    var _value = (typeof value !== "undefined"
        ? value
        : uncontrolledInputValue);
    var _placeholder = isReadMode && !hasError && !value ? "—" : placeholder;
    var _onClickIcon = useCallback(function (event) {
        if (disabled)
            return;
        if (onIconClick)
            onIconClick({ event: event, value: _value });
        if (hasReadModeStyles && _ref && _ref.current) {
            _ref.current.focus();
        }
    }, [_value, disabled, onIconClick, _ref, hasReadModeStyles]);
    var _onFocus = useCallback(function (e) {
        setFocused(true);
        if (onFocus)
            onFocus(e);
    }, [onFocus]);
    var _onBlur = useCallback(function (e) {
        var relatedTarget = e.relatedTarget;
        if (hasReadModeStyles &&
            relatedTarget !== null &&
            relatedTarget.contains(inputIconButtonRef.current)) {
            // prevent blur on input icon click for read mode
            return;
        }
        setFocused(false);
        if (onBlur)
            onBlur(e);
    }, [onBlur, hasReadModeStyles]);
    return (React.createElement(React.Fragment, null,
        React.createElement(InputContainer, __assign({ className: classnames("bn-textfield-input", disabled && "bn-textfield-input--disabled", isFocused && "bn-textfield-input--focused", hasError && "bn-textfield-input--error", isReadMode && "bn-textfield-input--readmode", inlinePrefixElement && "bn-textfield-input--prefixed") }, generateTestId("input-container")),
            inlinePrefixElement &&
                // <div className="bn-textfield-input-inlineprefix">
                inlinePrefixElement
        // </div>
        ,
            isMultiLine ? (React.createElement(TextareaAutosize, __assign({}, rest, { className: "bn-textfield-input-textarea", ref: _ref, value: isUncontrolled ? undefined : _value, onChange: _onChange, onFocus: _onFocus, onBlur: _onBlur, onClick: onClick, placeholder: _placeholder, minRows: minMultiLineRows, maxRows: maxMultiLineRows }, generateTestId("multi-line-input")))) : (React.createElement("input", __assign({}, rest, { className: "bn-textfield-input-textbox", ref: _ref, value: isUncontrolled ? undefined : _value, onChange: _onChange, onFocus: _onFocus, onBlur: _onBlur, onClick: onClick, disabled: disabled, placeholder: _placeholder }, generateTestId("single-line-input")))),
            inputIcon && !isReadMode && (React.createElement(IconButton, __assign({ ref: inputIconButtonRef, type: "button", onClick: _onClickIcon }, iconButtonAttributes, generateTestId("input-icon-button")), inputIcon))),
        hasError && errorMessage && (React.createElement(ErrorMessage, __assign({}, errorMessageAttributes, generateTestId("error"), generateTestId("error-message")), errorMessage))));
});
TextInput.displayName = "TextInput";
export default TextInput;
//# sourceMappingURL=index.js.map